import { yupResolver } from "@hookform/resolvers/yup";
import { Error, Form } from "apps/visits/packages/ui/src/components";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import VerificationInput from "react-verification-input";
import type * as yup from "yup";
import { Button } from "@/core/shared/v2/ui/button";
import type { ConfigName } from "../../types/config-name";
import { ResendCode } from "./resend-code";
import { useValidationSchema } from "./validation-schema";

export type CodeValidationFormValues = yup.InferType<ReturnType<typeof useValidationSchema>>;

type CodeValidationFormProps = {
  error?: string;
  onSubmit: (data: CodeValidationFormValues) => void;
  isLoading?: boolean;
  configName: ConfigName;
};

export const CodeValidationForm = ({ error, onSubmit, isLoading, configName }: CodeValidationFormProps) => {
  const { t } = useTranslation();

  const validationSchema = useValidationSchema();

  const { control, handleSubmit, errors } = useForm<CodeValidationFormValues>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
  });

  const handleOnSubmit: SubmitHandler<CodeValidationFormValues> = (data) => {
    onSubmit(data);
  };

  return (
    <Form onSubmit={handleSubmit(handleOnSubmit)}>
      <div className="mt-8 mb-4 flex max-w-2xl flex-col items-center max-lg:p-4 lg:w-screen">
        <div className="mb-10 flex max-w-lg items-center gap-4 text-center">
          <h1 className="font-serif text-2xl lg:text-3xl">
            {t(`VOUCHERS_DISTRIBUTION.CODE_VALIDATION_FORM.ENTER_CODE_SENT_EMAIL`)}
          </h1>
        </div>
        <Controller
          name="verificationCode"
          control={control}
          defaultValue=""
          render={({ onChange }) => (
            <div data-testid="code-validation-input">
              <VerificationInput
                placeholder=""
                validChars="0-9"
                classNames={{
                  container: "md:w-[408px] w-full gap-3 md:gap-6",
                  character: "border-grey-400",
                  characterInactive: "bg-white",
                }}
                onChange={onChange}
              />
            </div>
          )}
        />
        <span className="mt-3 block text-error">{errors.verificationCode?.message}</span>
        <ResendCode configName={configName} />
      </div>
      <div className="my-4 flex flex-col items-center gap-4">
        <Button
          className="w-full md:w-1/2"
          type="submit"
          size="lg"
          loading={isLoading}
          disabled={isLoading}
          data-testid="code-validation-submit"
        >
          {t("VOUCHERS_DISTRIBUTION.CODE_VALIDATION_FORM.SUBMIT")}
        </Button>
        <Error message={error} />
      </div>
    </Form>
  );
};
